import { Box, Flex, VStack } from '@chakra-ui/layout';
import { PinInput, PinInputField } from '@chakra-ui/pin-input';
import {
  Button,
  HStack,
  IconButton,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  UseDisclosureProps,
} from '@chakra-ui/react';
import { Icon } from '@common/components/Icon';
import { useInvestorAccountModel } from '@common/models/InvestorAccountModel';
import React, { useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';
import { DobSsnForm } from '.';
import { useSurveyBannerModel } from '../models/SurveyBannerModel';

type SSNProps = {
  form: UseFormReturn<DobSsnForm>;
  modalProps: UseDisclosureProps;
};

export const SSN: React.FC<SSNProps> = ({ form, modalProps }) => {
  const model = useSurveyBannerModel();
  const { saveDOBandSSN } = useInvestorAccountModel();
  const { ssn, dob } = form.watch();
  const [formError, setFormError] = useState<string | null>(null);
  const [showSSN, setShowSSN] = useState(false);

  const submitSSNDoB = async (dob: string, ssn: string) => {
    if (dob && ssn) {
      model.setLoading(true);
      try {
        const status = await saveDOBandSSN(dob, ssn);
        if (status.success) {
          model.determineAccountSetupState();
          model.setLoading(false);
        } else {
          setFormError('Something went wrong. Please try again');
          model.setLoading(false);
        }
      } catch {
        setFormError('Something went wrong. Please try again');
        model.setLoading(false);
      }
    } else {
      setFormError('Date of Birth and Socal Security Number must be filled out');
      model.setLoading(false);
    }
  };

  return (
    <ModalContent>
      <ModalHeader
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        borderBottomWidth={'1px'}
        borderBottomColor={'gray.300'}>
        <IconButton
          color="black"
          variant={'ghost'}
          aria-label="Back"
          icon={<Icon icon="chevron-left" />}
          onClick={() => model.prevStep()}
        />
        Social Security Number
        <IconButton
          color="black"
          variant={'ghost'}
          aria-label="Back"
          icon={<Icon icon="times" />}
          onClick={modalProps.onClose}
        />
      </ModalHeader>
      <ModalBody>
        <Flex flex={1} p="24px" justifyContent={'space-between'} flexDirection={'column'} display={'flex'}>
          <VStack gap={'10px'} mb="100px">
            <Box w={'100%'}>
              <TextBlock textAlign="start" weight="bold" fontSize={'20px'} lineHeight={'20px'} mb="10px">
                Please enter your SSN
              </TextBlock>
              <TextBlock fontSize={'14px'}>
                This is only used for regulatory requirements and will not affect your credit score
              </TextBlock>
            </Box>

            <HStack spacing={'5px'} width="auto" mx={'20px'}>
              <Controller
                control={form.control}
                name="ssn"
                rules={{
                  maxLength: 9,
                  minLength: 9,
                  required: 'Pin is required',
                }}
                render={({ field }) => (
                  <PinInput
                    mask={showSSN ? undefined : true}
                    autoFocus={form.formState.errors.ssn || !form.formState.isDirty ? true : false}
                    type="number"
                    placeholder=""
                    size={['sm', 'md', 'lg']}
                    otp={true}
                    onChange={(value) => {
                      field.onChange(value);
                      form.trigger();
                    }}>
                    <PinInputField tabIndex={1} />
                    <PinInputField tabIndex={2} />
                    <PinInputField tabIndex={3} />
                    <TextBlock>-</TextBlock>
                    <PinInputField tabIndex={4} />
                    <PinInputField tabIndex={5} />
                    <TextBlock>-</TextBlock>
                    <PinInputField tabIndex={6} />
                    <PinInputField tabIndex={7} />
                    <PinInputField tabIndex={8} />
                    <PinInputField
                      onKeyUp={(e) => {
                        if (e.key === 'Enter' && Boolean(dob) && Boolean(ssn) && ssn.length === 9) {
                          submitSSNDoB(dob, ssn);
                        }
                      }}
                      fontSize={'16px'}
                      tabIndex={9}
                    />
                  </PinInput>
                )}
              />
              <Box title={showSSN ? 'Hide' : 'Show'} cursor="pointer" onClick={() => setShowSSN(!showSSN)} pl={'10px'}>
                <Icon color={'blue.500'} icon={showSSN ? 'eye-slash' : 'eye'} />
              </Box>
              10
            </HStack>

            {formError && (
              <TextBlock fontSize={'12px'} color="red">
                {formError}
              </TextBlock>
            )}
          </VStack>
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button
          autoFocus={true}
          isLoading={model.loading}
          isDisabled={!dob || !ssn || ssn.length !== 9}
          w={'100%'}
          rightIcon={<Icon icon="chevron-right" />}
          mt={'10px'}
          onClick={() => submitSSNDoB(dob, ssn)}>
          Continue
        </Button>
      </ModalFooter>
    </ModalContent>
  );
};
